import { CommonModule } from '@angular/common'
import { Component, Input, OnDestroy, TemplateRef } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import { ToastrService } from '@app/services/toastr.service'
import { AuthService } from '@auth/services/auth.service'
import { CrewMember } from '@events/models/crew-member.interface'
import { CrewService } from '@events/services/crew.service'
import { NbCardModule, NbDialogRef, NbDialogService } from '@nebular/theme'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

/**
 * Component that handles an invited crew card
 */
@Component({
  selector: 'ngx-invite-item-card',
  templateUrl: './invite-item-card.component.html',
  styleUrls: ['./invite-item-card.component.scss'],
  imports: [CommonModule, MatIconModule, NbCardModule],
  standalone: true
})
export class InviteItemCardComponent implements OnDestroy {
  /** Subject that stops all hot observables */
  private _destroy$ = new Subject()
  /** On destroy implementation */
  ngOnDestroy() {
    this._destroy$.next(null)
    this._destroy$.complete()
  }
  /**
   * Crew member details
   */
  @Input()
  member: CrewMember
  /**
   * Is the crew member assigned
   */
  @Input()
  isAssign: boolean
  /**
   * Event slug
   */
  @Input()
  eventId: number
  /**
   * Dialog reference
   */
  private dialogRef: NbDialogRef<any>
  /**
   * Creates the component & injects it's dependencies
   * @param service CrewService
   * @param authService AuthService
   * @param dialogService NbDialogService
   * @param toastrService ToastrService
   */
  constructor(private service: CrewService, private authService: AuthService, private dialogService: NbDialogService, private toastrService: ToastrService) {}

  /**
   * Invite label
   */
  public get label() {
    return this.member.user?.full_name || this.member.user?.email
  }

  /**
   * Member logo
   */
  public get logo() {
    return (
      this.authService.currentWorkspace.logo?.replace('/upload/', '/upload/w_64,h_64,c_pad,q_100/') ||
      'https://res.cloudinary.com/contactless/image/upload/v1616928529/uploaded_public_files/dikd4yosq5lpe4qbuc6c.png'
    )
  }
  public get avatar() {
    return this.member.user?.profile_picture
      ? this.member.user?.profile_picture
      : 'https://res.cloudinary.com/contactless/image/upload/v1616928529/uploaded_public_files/dikd4yosq5lpe4qbuc6c.png'
  }

  /**
   * Is the member an admin of the crew
   */
  public get isAdmin() {
    return this.member.role === 'admin' ? false : true
  }

  /**
   * Unassigns the user from the event
   */
  unassign() {
    this.service
      .unassign(this.member.user.id, this.eventId)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        () => {
          this.toastrService.success('Removed', this.member.user.email + 'was unassigned as an event crew')
          this.close()
        },
        (error) => {
          this.toastrService.error('Whoops', 'We couldn’t unassign' + this.member.user.email + 'as part of your event crew')
          this.close()
        }
      )
  }

  /**
   * Opens the unassign dialog
   * @param deleteDialog TemplateRef
   */
  openDeleteModal(deleteDialog: TemplateRef<any>) {
    this.dialogRef = this.dialogService.open(deleteDialog, {
      context: this
    })
  }

  /**
   * Closes the dialog ref
   */
  close() {
    this.dialogRef.close()
  }

  /**
   * Invites a crew to an event/ assigns them
   * @param member Crew
   */
  public invite(member) {
    this.service
      .invite(member, this.eventId)
      .pipe(takeUntil(this._destroy$))
      .subscribe(
        () => {
          this.toastrService.success('Crew invited', 'Successfully invited ' + member.email + ' to your crew')
        },
        () => {
          this.toastrService.error('Unable to invite crew', member.email + ' was not invited, check the email and try again')
        }
      )
  }
}
